import {
  describeExcludeCurrent,
  ExcludeCurrentUnit,
  getFilterStringAndArrayFromOperandData,
  isDateColumn,
  isDateRangeOperator,
  isRelativeDateRangeOperator,
  isStandardDateOperator,
  SYSTEM_COLUMN_DISPLAY_NAME,
  TableColumnForTableExport,
  UNIT_MAPPINGS,
} from '@doss/common-all'
import { DataType, SystemGeneratedTableColumnType } from '@doss/prisma/types'
import type { TOptions } from 'i18next'
import { truncate } from 'lodash'
import {
  BooleanOperator,
  FilterOperator,
  TableFilterInput,
} from '~/common/generated/apollo-operations'
import { i18NextKeys } from '~/common/generated/i18n.generated'
import { ActiveUser } from '~/components/settings/types'

/**
 * Maps FilterOperator enum to a translatable key.
 */
function getOperatorTranslationKey(operator: FilterOperator): string {
  switch (operator) {
    case FilterOperator.CONTAINS:
      return 'table.filter_operator_contains'
    case FilterOperator.DOES_NOT_CONTAIN:
      return 'table.filter_operator_does_not_contain'
    case FilterOperator.STARTS_WITH:
      return 'table.filter_operator_starts_with'
    case FilterOperator.ENDS_WITH:
      return 'table.filter_operator_ends_with'
    case FilterOperator.EQUALS:
      return 'table.filter_operator_equals'
    case FilterOperator.IS_NOT_EQUAL_TO:
      return 'table.filter_operator_is_not_equal_to'
    case FilterOperator.GREATER_THAN:
      return 'table.filter_operator_greater_than'
    case FilterOperator.GREATER_THAN_OR_EQUAL_TO:
      return 'table.filter_operator_greater_than_or_equal_to'
    case FilterOperator.LESS_THAN:
      return 'table.filter_operator_less_than'
    case FilterOperator.LESS_THAN_OR_EQUAL_TO:
      return 'table.filter_operator_less_than_or_equal_to'
    case FilterOperator.IS_ANY_OF:
      return 'table.filter_operator_is_any_of'
    case FilterOperator.IS_NONE_OF:
      return 'table.filter_operator_is_none_of'
    case FilterOperator.IS_EMPTY:
      return 'table.filter_operator_is_empty'
    case FilterOperator.IS_NOT_EMPTY:
      return 'table.filter_operator_is_not_empty'
    case FilterOperator.IS_DUPLICATE:
      return 'table.filter_operator_is_duplicate'
    case FilterOperator.IN:
      return 'table.filter_operator_in'
    case FilterOperator.NOT_IN:
      return 'table.filter_operator_not_in'
    case FilterOperator.RANGE:
      return 'table.filter_operator_range'
    case FilterOperator.NOT_IN_RANGE:
      return 'table.filter_operator_not_in_range'
  }
}

/**
 * Maps date-specific string operators to translatable keys.
 */
function getDateOperatorTranslationKey(operator: string): string | null {
  // Standard date operators
  if (isStandardDateOperator(operator)) {
    switch (operator) {
      case 'is':
        return 'table.filter_operator_date_is'
      case 'not':
        return 'table.filter_operator_date_is_not'
      case 'after':
        return 'table.filter_operator_date_after'
      case 'onOrAfter':
        return 'table.filter_operator_date_on_or_after'
      case 'before':
        return 'table.filter_operator_date_before'
      case 'onOrBefore':
        return 'table.filter_operator_date_on_or_before'
      case 'isEmpty':
        return 'table.filter_operator_is_empty'
      case 'isNotEmpty':
        return 'table.filter_operator_is_not_empty'
      case 'isOn':
        return 'table.filter_operator_date_is_on'
      case 'notOn':
        return 'table.filter_operator_date_not_on'
    }
  }

  // Range operators
  if (isDateRangeOperator(operator)) {
    switch (operator) {
      case 'between':
        return 'table.filter_operator_date_between'
      case 'notBetween':
        return 'table.filter_operator_date_not_between'
    }
  }

  // Relative date operators - these need special handling with the value
  if (isRelativeDateRangeOperator(operator)) {
    switch (operator) {
      case 'this':
        return 'table.filter_operator_date_this'
      case 'last':
        return 'table.filter_operator_date_last'
      case 'notInTheLast':
        return 'table.filter_operator_date_not_in_the_last'
      case 'inTheNext':
        return 'table.filter_operator_date_in_the_next'
    }
  }

  return null
}

/**
 * Maps FilterOperator enum comparison operators to date-friendly translations.
 * For date columns, shows "is after" instead of ">", "is before" instead of "<", etc.
 */
function getDateFriendlyOperatorTranslationKey(operator: FilterOperator): string | null {
  switch (operator) {
    case FilterOperator.GREATER_THAN:
      return 'table.filter_operator_date_after'
    case FilterOperator.GREATER_THAN_OR_EQUAL_TO:
      return 'table.filter_operator_date_on_or_after'
    case FilterOperator.LESS_THAN:
      return 'table.filter_operator_date_before'
    case FilterOperator.LESS_THAN_OR_EQUAL_TO:
      return 'table.filter_operator_date_on_or_before'
    case FilterOperator.RANGE:
      return 'table.filter_operator_date_between'
    case FilterOperator.NOT_IN_RANGE:
      return 'table.filter_operator_date_not_between'
    default:
      return null
  }
}

/**
 * Maps FilterOperator enum to a human-readable display string using translations.
 * For date columns, uses date-specific operators (e.g., "is after" instead of ">").
 */
function getOperatorDisplayString(
  operator: FilterOperator | string,
  t: (key: i18NextKeys, options?: TOptions) => string,
  column?: TableColumnForTableExport,
): string {
  // Check if this is a date-specific string operator
  if (typeof operator === 'string') {
    const dateTranslationKey = getDateOperatorTranslationKey(operator)
    if (dateTranslationKey) {
      return t(dateTranslationKey as i18NextKeys)
    }
  }

  // For date columns, use date-friendly translations for comparison operators
  if (column && isDateColumn(column.resolvedSourceType)) {
    const dateFriendlyKey = getDateFriendlyOperatorTranslationKey(operator as FilterOperator)
    if (dateFriendlyKey) {
      return t(dateFriendlyKey as i18NextKeys)
    }
  }

  // Fall back to FilterOperator enum handling
  const translationKey = getOperatorTranslationKey(operator as FilterOperator)
  return t(translationKey as i18NextKeys)
}

/**
 * Checks if a column represents user data (USER type or system-generated user columns).
 */
function isUserColumn(column: TableColumnForTableExport | undefined, columnId: string): boolean {
  // Check if it's a system-generated user column
  if (
    columnId === SystemGeneratedTableColumnType.CREATED_BY ||
    columnId === SystemGeneratedTableColumnType.UPDATED_BY
  ) {
    return true
  }

  // Check if it's a regular USER column
  return column?.resolvedSourceType === DataType.USER
}

/**
 * Resolves user IDs to display names using the activeUsers list.
 * Returns the original values if no match is found.
 */
function resolveUserIdsToNames(values: string[], activeUsers: ActiveUser[] | undefined): string[] {
  if (!activeUsers || activeUsers.length === 0) {
    return values
  }

  const usersById = new Map(activeUsers.map((user) => [user.id, user]))

  return values.map((value) => {
    const user = usersById.get(value)
    if (user) {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')
      return fullName || user.email || value
    }
    return value
  })
}

/**
 * Gets the time unit display string for relative date filters.
 * Uses UNIT_MAPPINGS from common-all for singular forms, unit key for plural.
 */
function getTimeUnitDisplay(
  unit: string,
  value: number,
  t?: (key: i18NextKeys, options?: TOptions) => string,
): string {
  if (t) {
    const unitKey = value === 1 ? `table.time_unit_${unit}_singular` : `table.time_unit_${unit}`
    return t(unitKey as i18NextKeys)
  }
  // Fallback for filename generation - use UNIT_MAPPINGS for singular, unit key for plural
  if (value === 1 && unit in UNIT_MAPPINGS.TIME) {
    return UNIT_MAPPINGS.TIME[unit as keyof typeof UNIT_MAPPINGS.TIME]
  }
  return unit
}

/**
 * Extracts a display value from a filter's operand.
 * For relative date filters, returns a human-readable string like "5 days" or "this week".
 * For user columns, resolves user IDs to display names.
 */
function getFilterValueDisplay(
  filter: TableFilterInput,
  column: TableColumnForTableExport | undefined,
  t?: (key: i18NextKeys, options?: TOptions) => string,
  activeUsers?: ActiveUser[],
): string {
  const operator = filter.operator as string

  // For empty/not empty operators, no value to display
  if (
    operator === FilterOperator.IS_EMPTY ||
    operator === FilterOperator.IS_NOT_EMPTY ||
    operator === FilterOperator.IS_DUPLICATE ||
    operator === 'isEmpty' ||
    operator === 'isNotEmpty'
  ) {
    return ''
  }

  // Handle relative date operators - extract from operand's dynamicValue
  const dynamicValue = getDynamicValue(filter.operand)

  if (dynamicValue) {
    if (dynamicValue.type === 'this' && dynamicValue.unit) {
      // "this week", "this month", "this year"
      if (t) {
        return t(`table.time_unit_this_${dynamicValue.unit}` as i18NextKeys)
      }
      return `this ${dynamicValue.unit}`
    } else if (
      dynamicValue.type === 'relative' &&
      dynamicValue.unit &&
      dynamicValue.value != null
    ) {
      // "5 days", "2 weeks", etc.
      const unitDisplay = getTimeUnitDisplay(dynamicValue.unit, dynamicValue.value, t)
      const range = `${dynamicValue.value} ${unitDisplay}`
      const { excludeCurrent } = dynamicValue
      if (!excludeCurrent) {
        return range
      }
      // "7 days, excluding today" or "3 months, excluding this month"
      if (t) {
        return t(`date_picker.excluding_${excludeCurrent}_summary` as i18NextKeys, {
          range,
        })
      }
      return `${range}, ${describeExcludeCurrent(excludeCurrent)}`
    }
  }

  // Check if this is a user column - resolve IDs to names
  const isUserCol = isUserColumn(column, filter.columnId)

  // Use filterString or filterArray if available
  if (filter.filterString) {
    if (isUserCol) {
      const resolved = resolveUserIdsToNames([filter.filterString], activeUsers)
      return resolved[0]
    }
    return filter.filterString
  }

  if (filter.filterArray && filter.filterArray.length > 0) {
    let values = filter.filterArray
    if (isUserCol) {
      values = resolveUserIdsToNames(values, activeUsers)
    }
    if (values.length <= 2) {
      return values.join(', ')
    }
    return `${values[0]}, ${values[1]} +${values.length - 2}`
  }

  // Try to extract from operand
  if (filter.operand && column) {
    const { filterString, filterArray } = getFilterStringAndArrayFromOperandData(
      filter.operand,
      column.resolvedSourceType,
    )
    if (filterString) {
      if (isUserCol) {
        const resolved = resolveUserIdsToNames([filterString], activeUsers)
        return resolved[0]
      }
      return filterString
    }
    if (filterArray && filterArray.length > 0) {
      let values = filterArray
      if (isUserCol) {
        values = resolveUserIdsToNames(values, activeUsers)
      }
      if (values.length <= 2) {
        return values.join(', ')
      }
      return `${values[0]}, ${values[1]} +${values.length - 2}`
    }
  }

  return ''
}

/**
 * Gets the column name for a filter, handling both regular and system-generated columns.
 */
function getColumnName(
  filter: TableFilterInput,
  columnsById: Map<string, TableColumnForTableExport>,
): string {
  // Check if it's a system-generated column
  const systemColumnType = filter.columnId as SystemGeneratedTableColumnType
  if (SYSTEM_COLUMN_DISPLAY_NAME[systemColumnType]) {
    return SYSTEM_COLUMN_DISPLAY_NAME[systemColumnType]
  }

  // Look up in regular columns
  const column = columnsById.get(filter.columnId)
  return column?.name ?? filter.columnId
}

export interface FilterDisplayInfo {
  columnName: string
  operatorDisplay: string
  valueDisplay: string
  fullDisplay: string
}

interface DynamicValue {
  type?: string
  operator?: string
  unit?: string
  value?: number
  excludeCurrent?: ExcludeCurrentUnit
}

/**
 * Safely extracts the dynamicValue from a filter's operand.
 * The dynamicValue can be at operand.dynamicValue or operand.data.dynamicValue.
 */
function getDynamicValue(operand: unknown): DynamicValue | undefined {
  if (!operand || typeof operand !== 'object') {
    return undefined
  }

  const obj = operand as Record<string, unknown>

  // Check operand.data.dynamicValue
  if (obj.data && typeof obj.data === 'object') {
    const data = obj.data as Record<string, unknown>
    if (data.dynamicValue && typeof data.dynamicValue === 'object') {
      return data.dynamicValue as DynamicValue
    }
  }

  // Check operand.dynamicValue directly
  if (obj.dynamicValue && typeof obj.dynamicValue === 'object') {
    return obj.dynamicValue as DynamicValue
  }

  return undefined
}

/**
 * Extracts the effective operator for display from a filter.
 * For relative date filters, the actual operator (last, this, inTheNext, notInTheLast)
 * is stored in operand.data.dynamicValue.operator, not in filter.operator.
 */
function getEffectiveOperator(filter: TableFilterInput): string {
  const dynamicValue = getDynamicValue(filter.operand)

  if (dynamicValue?.operator && isRelativeDateRangeOperator(dynamicValue.operator)) {
    return dynamicValue.operator
  }

  // For "this" period (this week/month/year), the type is 'this' and acts as the operator
  if (dynamicValue?.type === 'this') {
    return 'this'
  }

  return filter.operator
}

/**
 * Formats a single filter into a human-readable display object.
 */
export function formatFilterForDisplay(
  filter: TableFilterInput,
  columnsById: Map<string, TableColumnForTableExport>,
  t: (key: i18NextKeys, options?: TOptions) => string,
  activeUsers?: ActiveUser[],
): FilterDisplayInfo {
  const columnName = getColumnName(filter, columnsById)
  const column = columnsById.get(filter.columnId)

  // Get the effective operator (may be extracted from operand for date filters)
  const effectiveOperator = getEffectiveOperator(filter)
  const operatorDisplay = getOperatorDisplayString(effectiveOperator, t, column)
  const valueDisplay = getFilterValueDisplay(filter, column, t, activeUsers)

  // Build full display string
  let fullDisplay = `${columnName} ${operatorDisplay}`
  if (valueDisplay) {
    fullDisplay += ` "${valueDisplay}"`
  }

  return {
    columnName,
    operatorDisplay,
    valueDisplay,
    fullDisplay,
  }
}

/**
 * Formats multiple filters into display info array.
 */
export function formatFiltersForDisplay(
  filters: TableFilterInput[],
  columns: TableColumnForTableExport[],
  t: (key: i18NextKeys, options?: TOptions) => string,
  activeUsers?: ActiveUser[],
): FilterDisplayInfo[] {
  const columnsById = new Map(columns.map((col) => [col.id, col]))
  return filters.map((filter) => formatFilterForDisplay(filter, columnsById, t, activeUsers))
}

/**
 * Generates a filename-safe summary of the filters applied.
 * Examples:
 * - "Status-Pending" (single filter with simple value)
 * - "Status-Pending-Date-This-Week" (two filters)
 * - "filtered-3-conditions" (3+ filters, abbreviated)
 */
export function generateFilterFilenameSegment(
  filters: TableFilterInput[],
  columns: TableColumnForTableExport[],
  maxLength = 50,
): string {
  if (filters.length === 0) {
    return ''
  }

  const columnsById = new Map(columns.map((col) => [col.id, col]))

  // For 3+ filters, use abbreviated form
  if (filters.length >= 3) {
    return `filtered-${filters.length}-conditions`
  }

  // For 1-2 filters, build a descriptive segment
  const parts: string[] = []
  for (const filter of filters) {
    const columnName = getColumnName(filter, columnsById)
    const column = columnsById.get(filter.columnId)
    const valueDisplay = getFilterValueDisplay(filter, column)

    // Sanitize for filename: remove special chars, replace spaces with dashes
    const sanitizedColumn = columnName.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-')
    let sanitizedValue = valueDisplay.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-')

    // Truncate long values
    if (sanitizedValue.length > 15) {
      sanitizedValue = sanitizedValue.substring(0, 15)
    }

    if (sanitizedValue) {
      parts.push(`${sanitizedColumn}-${sanitizedValue}`)
    } else {
      parts.push(sanitizedColumn)
    }
  }

  const result = parts.join('-')

  // Truncate if too long
  if (result.length > maxLength) {
    return truncate(result, { length: maxLength, omission: '' })
  }

  return result
}

/**
 * Generates a short human-readable summary of filters for display.
 * Examples:
 * - "Status is Pending" (single filter)
 * - "Status is Pending and Date is This Week" (two filters)
 * - "3 filters applied" (many filters)
 */
export function generateFilterDisplaySummary(
  filters: TableFilterInput[],
  columns: TableColumnForTableExport[],
  t: (key: i18NextKeys, options?: TOptions) => string,
  booleanOperator: BooleanOperator = BooleanOperator.AND,
  activeUsers?: ActiveUser[],
): string {
  if (filters.length === 0) {
    return ''
  }

  if (filters.length >= 3) {
    return `${filters.length} filters`
  }

  const displayInfos = formatFiltersForDisplay(filters, columns, t, activeUsers)
  const connector = booleanOperator === BooleanOperator.AND ? ' and ' : ' or '

  return displayInfos
    .map((info) => {
      if (info.valueDisplay) {
        return `${info.columnName} ${info.operatorDisplay} "${truncate(info.valueDisplay, { length: 20 })}"`
      }
      return `${info.columnName} ${info.operatorDisplay}`
    })
    .join(connector)
}
